/* eslint-disable @typescript-eslint/no-explicit-any */
import { common } from "@shared/constants";
import { SeatingLocationMapIdType } from "@shared/constants/enums/enums-types";
import {
  TicketItemGroupPermissionTypeLocation,
  TicketItemGroupPermissionTypeSeat,
  TicketItemGroupType,
} from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import Konva from "konva";
import { type BestAvailableQueryActions } from "../features/BestAvailable/hooks/useBestAvailableQueryParams";
import type { SeatingItemPropertyMap } from "./seating-property-types";

// Curved row display types
export interface CurvePoints {
  start: { x: number; y: number };
  control: { x: number; y: number };
  end: { x: number; y: number };
}

export interface CurvedRowDisplay {
  curveEnabled: boolean;
  curvePoints?: CurvePoints;
  invertSeatOrder?: boolean;
}

export type BestAvailableItemType = {
  seat_options: Array<{ seat_group: BestAvailableSeatingType; score: number }>;
  location_options: BestAvailableLocationType;
};

export type BestAvailableLocationType = Array<
  TicketItemGroupPermissionTypeSeat & {
    id: number;
    max_price: string;
    min_price: string;
    ttlps: Array<{
      id: number;
      ticket_type_id: number;
      membership_level_id: number;
      price: string;
    }>;
    score: number;
  }
>;

export type SingleBestAvailableSeatingType =
  TicketItemGroupPermissionTypeSeat & {
    max_price: string;
    min_price: string;
    ttsps: Array<{
      id: number;
      ticket_type_id: number;
      membership_level_id: number;
      price: string;
    }>;
  };

export type BestAvailableSeatingType = Array<SingleBestAvailableSeatingType>;

export type BestAvailableSeatingQueryParamsType = {
  event?: number;
  quantity?: number;
  package?: number;
  ticket_types?:
    | Record<string | number, number>
    | Record<string | number, never>;
  is_accessible?: boolean;
  membership_group?: number;
  membership_levels?: Record<string | number, number>;
  result_limit?: number;
  segment_id?: number;
  allPermissions?: boolean;
  membership_benefit?: number;
};

export type BestAvailableSeatingQueryParamsActionType = {
  type: BestAvailableQueryActions;
  payload: BestAvailableSeatingQueryParamsType;
};

export type SeatingSelectReturnType = {
  itemTypeId: number;
  permissionId: number;
  requestAmount?: number;
  minPurchaseLimit?: number;
  ticketAuthArray: { id: number; authorized: boolean }[];
  payWhatYouCanPrice?: string | null;
  itemGroupId?: number;
};

export type SeatingCartItemType = {
  id: number;
  seat?: TicketItemGroupPermissionTypeSeat;
  location?: TicketItemGroupPermissionTypeLocation;
  resale_submission_id?: number;
  permissionId: number;
  itemGroup?: TicketItemGroupType;
};

export type MapPermissionsType = {
  // fields shared on seats and locations
  seatingItemTypeId: number;
  seatingItemPermissionId?: number;
  seatingItemPasswordProtected?: boolean;
  seatingItemResaleId?: number;
  seatingItemResaleStatus?: string;
  seatingItemMinPurchaseLimit?: number | null;
  seatingItemType?: SeatingItemPropertyMap;
  locationPendingResaleCount?: number | 0;
  seatPurchaser?: {
    name_on_ticket: string;
    email: string;
    transaction_id: string;
    basket_id: number;
    invoice_id: number;
  };
  seatStatus?: string;

  // fields on locations only
  capacity_limit?: number;
  location?: number;
  location_capacity?: number;
  usage_remaining?: number;

  selectedPwycPrice?: string;
};

export type SeatWithLocationDataType = TicketItemGroupPermissionTypeSeat & {
  permissionId?: number;
  locationData?: PublicBaseLocationSerializer;
  node?: Konva.Node;
  label?: string;
  price?: string;
  ticketTypeId?: number;
  membershipLevelId?: number;
  name?: string;
  qty?: number;
  data?: LocationDataType;
};

export type FormattedSeatWithLocationDataType = SeatWithLocationDataType &
  FormattedSeatType;

export type LocationDataType = {
  capacity: number;
  id: number;
  identifier: string;
  node: Konva.Node;
  count?: number;
  section?: string;
};

export type CartPermissionType = {
  permissionId: number;
  itemTypeId?: number;
  seat?: SeatWithLocationDataType;
  location?: TicketItemGroupPermissionTypeLocation;
  resaleId?: number;
};

export type PositionType = { x: number; y: number };

export type TransformType = {
  x: number;
  y: number;
  rotation: number;
};

export type PointType = [number, number];

export type SeatDataType = {
  $data: TicketItemGroupPermissionTypeSeat;
  $name: string;
  $permissions: Array<MapPermissionsType>;
  locationData: PublicBaseLocationSerializer;
  node: Konva.Node;
};

export type SeatHighlightData = {
  id?: number;
  seatIdentifiers: string[];
  segmentIdentifier: string;
  locationIdentifier: string;
  permissionsMap?: Record<string | number, number>;
  pricingMap?: Record<string | number, string>;
  ticketTypesMap?: Record<string | number, number>;
  membershipLevelsMap?: Record<string | number, number>;
  locationData?: PublicBaseLocationSerializer;
  seatIds?: number[];
};

export type FormattedSeatType = {
  $data: SeatWithLocationDataType;
  itemGroupParent?: number | null;
  permissionId: number;
  itemTypeId?: number;
  minPurchaseLimit?: number | null;
  resaleId?: number;
  quantity?: number;
  payWhatYouCanPrice?: string | null;
  itemGroupId?: number;
  membershipLevelId?: number;
};

export type SeatUsageType = {
  id: number;
  seat: number;
  event: number;
};

export type SelectGAType = {
  $data: LocationDataType;
  $name: string;
  $permissions: MapPermissionsType[];
};

export type SelectSeatType = {
  $data: SeatWithLocationDataType;
  $name?: string;
  $permissions?: MapPermissionsType[];
  animateSpinner?: () => void;
};

export type ItemsOnSeatType = {
  $data: null | SeatWithLocationDataType | LocationDataType;
  $name: string;
  $permissions: MapPermissionsType[];
};

export type ItemSelectReturnType = {
  itemTypeId: number;
  permissionId: number;
  requestAmount?: number;
  minPurchaseLimit?: number;
  ticketAuthArray: Array<{ id: number; authorized: boolean }>;
  payWhatYouCanPrice?: string | null;
  itemGroupId?: number;
};

export enum MultiSeatSelectActions {
  Add = "add",
  Remove = "remove",
}

export type SeatsToMultiSelectType = {
  seats: Array<SeatDataType>;
  itemIdsToAdd: Array<number>;
  itemIdsToRemove: Array<number>;
  action: MultiSeatSelectActions;
};

/**
 * Return type for modals that handle seat selection/removal
 * Examples: isolated seat selection/removal, full-purchase seat selection/removal
 */
export type SeatSelectionModalReturnType = Array<{
  seatId?: number;
  itemType?: number;
  itemTypeId?: number;
  permissionId: number;
  $data: SeatWithLocationDataType;
  itemGroupParent?: number;
  minPurchaseLimit?: number;
  resaleId?: number;
  payWhatYouCanPrice?: string | null;
}>;

export interface PublicBaseLocationSerializer {
  id: number;
  identifier: string;
  transform: TransformType;
  type: SeatingLocationMapIdType;
  capacity: number;
  offset?: number;
  step?: number;
  display: any;
  spacing?: number;
  full_purchase_required: boolean;
  segment_id?: number;
  space_id?: number;
  venue_id?: number;
  seats: TicketItemGroupPermissionTypeSeat[];
}

export interface TicketSeatPermissionType {
  id?: any;
  tt_seat_permission_id?: any;
  segment_id?: any;
  resale_submission_id?: any;
  basket?: any;
  status?: any;
  resale_submission_status?: any;
}

export interface MembershipSeatPermissionType {
  id?: any;
  seat_permission_id?: any;
  segment_id?: any;
}

export interface PublicBaseSeatPermissionsV2Serializer {
  ticket_types?: Array<{
    id: number;
    seats: Array<TicketSeatPermissionType>;
  }>;
  membership_levels?: Array<{
    id: number;
    seats: Array<MembershipSeatPermissionType>;
  }>;
}

export interface PublicTTSeatPermissionSerializer {
  id: number;
  ticket_type: number;
  membership_level: number;
  seat: number;
  status: string;
  resale_submission_id?: number;
  resale_submission_status?: string;
}

export interface TicketLocationPermissionType {
  id?: any;
  capacity_limit?: any;
  tt_location_permission_id?: any;
  location_capacity?: any;
  segment_id?: any;
  usage_remaining?: any;
  location_pending_resale_count?: number;
}

export interface MembershipLocationPermissionType {
  id?: any;
  capacity_limit?: any;
  location_permission_id?: any;
  location_capacity?: any;
  segment_id?: any;
  usage_remaining?: any;
}

export interface PublicBaseLocationPermissionsV2Serializer {
  membership_levels?: Array<{
    id: number;
    locations: Array<MembershipLocationPermissionType>;
  }>;
  ticket_types?: Array<{
    id: number;
    locations: Array<TicketLocationPermissionType>;
  }>;
}

export interface PublicBaseLocationPermissionsSerializer {
  id: number;
  capacity_limit: number;
  location: number;
  ticket_type: number;
  membership_level: number;
  stats?: {
    fully_claimed?: boolean;
    last_updated?: string;
    total_claimed?: number;
  };
}

export interface PublicBaseSegmentSetSerializer {
  display: {
    points: PointType[];
  };
  hide_location_names: boolean;
  id: number;
  identifier: string;
  location_set: PublicBaseLocationSerializer[];
  transform: TransformType;
}

export interface PublicBaseSpaceSerializer {
  id: number;
  name: string;
  use_clickers: boolean;
  background_file?: string;
  background_transform?: {
    height: number;
    width: number;
    x: number;
    y: number;
  };
  segment_set?: PublicBaseSegmentSetSerializer[];
  display_advanced_seat_colors: boolean;
  always_display_seats_and_locations: boolean;
  seat_size: string;
  seat_spacing: number;
  seat_count: number;
  hide_sections_on_map?: boolean;
}

export type VenueBaseSpaceSerializer = PublicBaseSpaceSerializer & {
  venue: number;
};

export type VenueBaseLocationSerializer = PublicBaseLocationSerializer;

export type VenueBaseSegmentSetSerializer = PublicBaseSegmentSetSerializer;

export type PublicTTPermissionsPayloadType = {
  location?: number;
  seat?: number;
  space: number;
  ticket_type__event?: number;
  event?: number;
  membership_benefit?: number;
  basket_hold?: number;
  membership_level_ticket_types?: boolean;
};

export type VenueBasedSeatPermissionsSerializer =
  PublicBaseSeatPermissionsV2Serializer & {
    basket: {
      email: string;
      name_on_ticket: string;
    };
    status?: string;
  };
export interface SeatingPermissionsResponse {
  ticket_types?: Array<{
    id: number;
    seats: Array<TicketSeatPermissionType>;
  }>;
  exchange_seat_permissions?: number[] | string;
}

export enum SeatStatus {
  SOLD = "sold",
  RESERVED = "reserved",
  AVAILABLE = "available",
  BLOCKED = "blocked",
}

export type SeatsToAddType = {
  item: {
    mapItemType: common.SeatingMapItemType;
    accepted_terms: boolean;
    $data: SeatWithLocationDataType;
    itemGroupParent?: number | null | undefined;
    itemTypeId?: number;
    permissionId: number;
    resaleId?: number | undefined;
    minPurchaseLimit?: number | null | undefined;
    quantity?: number | undefined;
  };
  quantity: number;
  parent: number | null | undefined;
  pay_what_you_can_price: string | null | undefined;
};

export type PublicBasedTTSeatPermissionsRenewalPermissionsSerializer = {
  membership_levels: Array<{ id: number; seat_permissions: Array<number> }>;
};
